import { useContext, useState } from 'react';
import { ToastNotificationContext } from '../../../../common/toast-notification';
import { useDialog } from '../../../../common/dialog/useDialog';
import { DialogError } from '../../../../common/business-error';
import { addParams, fetchWithTokenRefresh } from '../../../../../api/fetchWithTokenRefresh';
import endpoints from '../../../../../api/endpoints';
import { handleResponseError } from '../../../../../api/handleResponseError';
// eslint-disable-next-line max-len
import { userInvitationBusinessError } from '../../../portal/pages/sub-accounts-management/inactive/user-invitation/businessErrors';

import { ToastProps } from '../../../../common/toast-notification/interfaces';

type SuccessfullApiResponse = {
  message: string;
  status: string;
};

type FailureApiResponse = {
  message: string;
  code: FailureApiResponseCode;
};

enum FailureApiResponseCode {
  NO_INVITATION = 'NO_INVITATION',
  INVITATION_CODE_VERIFICATION_ERROR = 'INVITATION_CODE_VERIFICATION_ERROR',
  INVITATION_EXPIRED = 'INVITATION_EXPIRED'
}

interface Props {
  handleSuccess: () => void;
}

export const useInvitationConfirm = ({ handleSuccess }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DialogError>(null);

  const handleConfirmation = async (code: string) => {
    try {
      setLoading(true);
      const response = await fetchWithTokenRefresh(
        endpoints.clientInvitationConfirm,
        addParams('POST', { invitationCode: code })
      );
      const jsonResponse = await response.json();

      if (response.status === 200) {
        const parsed: SuccessfullApiResponse = jsonResponse;
        openToast({
          message: parsed.message,
          severity: 'success'
        });
        handleSuccess();
      } else if (response.status === 400) {
        const parsed: FailureApiResponse = jsonResponse;

        switch (parsed.code) {
          case FailureApiResponseCode.INVITATION_EXPIRED:
          case FailureApiResponseCode.NO_INVITATION: {
            const toastResponse: ToastProps = {
              message: parsed.message,
              severity: 'error'
            };
            openToast(toastResponse);
            setError({ message: toastResponse.message, severity: 'error' });
            break;
          }
          case FailureApiResponseCode.INVITATION_CODE_VERIFICATION_ERROR: {
            const toastResponse: ToastProps = {
              message: parsed.message,
              severity: 'error'
            };
            openToast(toastResponse);
            setError({ message: toastResponse.message, severity: 'error' });
            break;
          }
          default: {
            openToast({ message: 'unknown error', severity: 'error' });
            setError({ message: 'unknown error', severity: 'error' });
            break;
          }
        }
      } else {
        await handleResponseError(response);
      }

      setLoading(false);
    } catch (e) {
      const { message, severity } = userInvitationBusinessError();

      setError({ message, severity });
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    isDialogOpen,
    loading,
    error,
    handleOpenDialog,
    closeDialog,
    handleConfirmation
  };
};
